// The KPI DETAIL view's list + rail sections (KPI Screen v2, the "Signal" re-layout), split from
// MetricDetail.tsx: the closed-loop "What moved it" Task list, the on-demand Value EVIDENCE block
// (G-011), and the housekeeping facts. Every rendered fact derives from the projection (G-182, P-SELF);
// no internal id renders (G-197). Tokens only (G-181).

import { useState } from 'react';
import type { MetricCard, MetricEvidenceSheet, MetricLinkedTask } from '../projections.ts';
import { Icon } from '../../app/Icon.tsx';
import { Btn, TrendArrow } from '../primitives.tsx';
import { EvidenceSheet } from './EvidenceSheet.tsx';
import { fmtMetricValue, fmtDayMonth } from './metric-format.ts';

/** One linked-Task row (prototype .tl-item): lettered/status mark, state · date meta, title, brief. */
function LinkedTaskRow({
  task,
  letter,
}: {
  readonly task: MetricLinkedTask;
  readonly letter: string | undefined;
}): React.JSX.Element {
  const status = task.state === 'done' ? 'done' : task.state === 'dropped' ? 'drop' : 'live';
  const statusLabel =
    task.state === 'done' ? 'completed' : task.state === 'dropped' ? 'dropped' : 'live';
  return (
    <div className="tl-item" data-linked-task={task.id}>
      <span
        className={`tl-mark ${status}${letter !== undefined ? ' lettered' : ''}`}
        aria-hidden="true"
      >
        {letter ?? ''}
      </span>
      <div className="tl-body">
        <div className="tl-meta">
          <span className={`tl-stat ${status}`}>{statusLabel}</span> ·{' '}
          <span className="tl-date">{fmtDayMonth(task.updatedAt)}</span>
        </div>
        <div className="tl-title">{task.title}</div>
        {task.brief !== null && task.brief.length > 0 ? (
          <div className="tl-desc">{task.brief}</div>
        ) : null}
      </div>
    </div>
  );
}

/** The closed-loop "What moved it" list; empty offers a chat handoff to aim a Task at this KPI. */
export function DetailTasks({
  metric,
  letterByTaskId,
  onStartTask,
}: {
  readonly metric: MetricCard;
  readonly letterByTaskId: ReadonlyMap<string, string>;
  readonly onStartTask: () => void;
}): React.JSX.Element {
  const tasks = metric.linkedTasks;
  const done = tasks.filter((task) => task.state === 'done').length;
  const dropped = tasks.filter((task) => task.state === 'dropped').length;
  const live = tasks.length - done - dropped;
  const tally =
    tasks.length === 0
      ? '0 total'
      : `${tasks.length} total · ${done} done · ${live} live${dropped > 0 ? ` · ${dropped} dropped` : ''}`;
  return (
    <div className="md-tasks">
      <div className="group-head">
        <h3 className="h-section">What moved it</h3>
        <span className="mono-label">{tally}</span>
      </div>
      {tasks.length === 0 ? (
        <div className="tl-none">
          <span>
            Nothing has been aimed at this KPI yet. Start a task from here and it will appear on
            this list — and on the chart at the week it lands.
          </span>
          <Btn
            kind="ghost"
            sm
            ariaLabel={`Start a task for KPI: ${metric.name}`}
            onClick={onStartTask}
          >
            Start a task
          </Btn>
        </div>
      ) : (
        <div className="tl-list panel">
          {tasks.map((task) => (
            <LinkedTaskRow key={task.id} task={task} letter={letterByTaskId.get(task.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

/** The provenance block: the latest Value's stored working, loaded ON DEMAND (G-011). */
export function DetailEvidence({
  metric,
  loadEvidence,
}: {
  readonly metric: MetricCard;
  readonly loadEvidence: (valueId: string) => Promise<MetricEvidenceSheet>;
}): React.JSX.Element | null {
  const [showEvidence, setShowEvidence] = useState(false);
  const value = metric.latestValue;
  if (value === null) return null;
  return (
    <div className="kd-block">
      <h3 className="h-section">Where this figure comes from</h3>
      <p className="kd-block-note">
        The stored working behind {fmtMetricValue(value.value)} for {fmtDayMonth(value.periodStart)}{' '}
        → {fmtDayMonth(value.periodEnd)} — method, sources, calculation, and limits.
      </p>
      <Btn
        kind="ghost"
        sm
        ariaLabel={`${showEvidence ? 'Hide' : 'Show'} value evidence: ${metric.name}`}
        ariaExpanded={showEvidence}
        onClick={() => setShowEvidence((open) => !open)}
      >
        {showEvidence ? 'Hide the working' : 'Show the working'}
      </Btn>
      {showEvidence ? <EvidenceSheet valueId={value.id} load={loadEvidence} /> : null}
    </div>
  );
}

/** The housekeeping facts: cadence, latest recorded period, recent trend direction, gate custody. */
export function DetailHousekeeping({ metric }: { readonly metric: MetricCard }): React.JSX.Element {
  const value = metric.latestValue;
  return (
    <div className="kd-house">
      <div className="mono-label">Housekeeping</div>
      {metric.cadenceLabel !== null ? (
        <div className="row">
          <span className="ic" aria-hidden="true">
            <Icon name="clock" size={14} />
          </span>
          <span>Collected {metric.cadenceLabel}</span>
        </div>
      ) : null}
      {value !== null ? (
        <div className="row">
          <span className="ic" aria-hidden="true">
            <Icon name="pulse" size={14} />
          </span>
          <span>
            Latest period {fmtDayMonth(value.periodStart)} → {fmtDayMonth(value.periodEnd)} ·{' '}
            {value.state}
          </span>
        </div>
      ) : null}
      <div className="row">
        <span className="ic" aria-hidden="true">
          <Icon name="pulse" size={14} />
        </span>
        <span>
          Recent trend <TrendArrow direction={metric.recentTrendDirection} />
        </span>
      </div>
      <div className="row">
        <span className="ic" aria-hidden="true">
          <Icon name="approvals" size={14} />
        </span>
        <span>Activation and value confirmation are gates only you hold</span>
      </div>
    </div>
  );
}
